import { useLocalStorageState } from "~/hooks/use-local-storage-state";

const POPOUT_MODE_STORAGE_KEY = "istari-ai-chat-popout-mode";

// How the popped-out chat window behaves:
// - "floating": Document Picture-in-Picture — a browser-managed window that stays always-on-top over
//   every application. Best for keeping the chat visible while working in other apps.
// - "window": a standard separate browser window (window.open) — behaves like any window, so it sits
//   behind the main window or other apps when those are focused. Best when you don't want the chat
//   floating over everything.
export type ChatPopoutMode = "floating" | "window";

// Default to a standard window: a normal window that follows focus/z-order is the least-surprising
// behavior. Users who want the chat to stay on top of every app opt into "floating" in Application
// settings (where it's available — Chromium only).
export const CHAT_POPOUT_MODE_DEFAULT: ChatPopoutMode = "window";

// Whether the always-on-top "floating" mode is available — Document Picture-in-Picture is
// Chromium-only. When false, "floating" silently falls back to a standard window, so the settings UI
// disables the option to avoid offering a choice that doesn't do anything different.
export function isChatFloatingSupported(): boolean {
  return typeof window !== "undefined" && "documentPictureInPicture" in window;
}

export function useChatPopoutMode() {
  const [popoutMode, setPopoutMode] = useLocalStorageState<ChatPopoutMode>(
    POPOUT_MODE_STORAGE_KEY,
    CHAT_POPOUT_MODE_DEFAULT,
  );
  return { popoutMode, setPopoutMode };
}
